import React from "react";

import { CloseRounded, SendRounded } from "@mui/icons-material";
import { Box, DialogContent, TextField } from "@mui/material";

import useModal from "../../config/context/modalContext";
import { GreyPallet } from "../../config/styles";

import MainSolidButton from "../MainSolidButton";
import { ModalTransition } from "../ModalTransition";
import PageHeader from "../PageHeader";

import { httpsCallable } from "firebase/functions";
import useFirebase from "../../config/context/firebaseContext";
import { Dev } from "../../config/util/consoleUtil";
import { ModalBaseType } from "../../types/common";
import ChipMain from "../InputMain/ChipMain";
import { WideDialog } from "../MainModalSimple/styles";

type ModalNotifyProps = ModalBaseType & {
  fcmToken: string;
};

export default function ModalNotify({ onClose, fcmToken }: ModalNotifyProps) {
  const [open, setOpen] = React.useState(true);

  const { pushAlert } = useModal();

  const { functions } = useFirebase();

  const [titleField, setTitleField] = React.useState("");
  const [bodyField, setBodyField] = React.useState("");
  const [template, setTemplate] = React.useState<"custom" | "post_ap" | "post_re" | "new_message">(
    "custom"
  );

  const [loadingSend, setLoadingSend] = React.useState(false);

  const handleSend = async () => {
    try {
      setLoadingSend(true);
      const sendNotificationToUser = httpsCallable(functions, "sendNotificationToUser");

      const result = await sendNotificationToUser({
        fcmToken,
        title: titleField,
        body: bodyField,
      });

      pushAlert({
        type: "success",
        message: "Notificação enviada",
      });

      handleClose();
    } catch (error: any) {
      console.log(...new Dev("handleSend", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao enviar notificação",
      });
    } finally {
      setLoadingSend(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 200);
  };

  React.useEffect(() => {
    if (template === "custom") {
      setTitleField("");
      setBodyField("");
    } else if (template === "post_ap") {
      setTitleField("Postagem Aprovada ✅");
      setBodyField("Sua postagem já está visível");
    } else if (template === "post_re") {
      setTitleField("Postagem Reprovada ⛔");
      setBodyField("Sua postagem não foi aprovada");
    } else if (template === "new_message") {
      setTitleField("Nova Mensagem");
      setBodyField("Você possui uma nova mensagem do Dev");
    }
  }, [template]);

  return (
    <WideDialog
      fullWidth={true}
      open={open}
      onClose={handleClose}
      TransitionComponent={ModalTransition}
    >
      <DialogContent>
        <PageHeader
          size="subtitle"
          title={"Notificar Usuário"}
          rightIcon={<CloseRounded />}
          onRightAction={handleClose}
        />

        <Box>
          <Box mt={4} gap={1} display={"flex"} flexWrap={"wrap"}>
            <ChipMain
              options={[
                { name: "Custom", value: "custom" },
                { name: "Post Aprovado", value: "post_ap" },
                { name: "Post Reprovado", value: "post_re" },
                { name: "Nova Mensagem", value: "new_message" },
              ]}
              onChange={(value) =>
                setTemplate(value as "custom" | "post_ap" | "post_re" | "new_message")
              }
              value={template}
            />
          </Box>

          <Box mt={2}>
            <TextField
              fullWidth
              label="Título"
              variant="outlined"
              sx={{
                "& .MuiFormLabel-root": {
                  fontWeight: "bold",
                  color: `${GreyPallet.Regular} !important`,
                },

                "& .MuiOutlinedInput-root": {
                  borderRadius: "15px",
                  height: 60,
                },

                "& .MuiInputBase-input": {
                  fontWeight: "bold",
                  fontSize: 16,
                  color: GreyPallet.Regular,
                },

                "& .MuiSvgIcon-root": {
                  fontSize: 26,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "3px !important",
                  borderColor: `${GreyPallet.Regular} !important`,
                  borderStyle: "solid",
                  "& .Mui-focused": {
                    borderColor: `${GreyPallet.Regular} !important`,
                  },
                },
              }}
              value={titleField}
              onChange={(e) => setTitleField(e.target.value)}
            />
          </Box>

          <Box mt={2}>
            <TextField
              autoCapitalize="sentences"
              fullWidth
              multiline
              rows={4}
              label="Corpo"
              variant="outlined"
              sx={{
                "& .MuiFormLabel-root": {
                  fontWeight: "bold",
                  color: `${GreyPallet.Regular} !important`,
                },

                "& .MuiOutlinedInput-root": {
                  borderRadius: "15px",
                },

                "& .MuiInputBase-input": {
                  fontWeight: "bold",
                  fontSize: 16,
                  color: GreyPallet.Regular,
                },

                "& .MuiSvgIcon-root": {
                  fontSize: 26,
                },

                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "3px !important",
                  borderColor: `${GreyPallet.Regular} !important`,
                  borderStyle: "solid",
                  "& .Mui-focused": {
                    borderColor: `${GreyPallet.Regular} !important`,
                  },
                },
              }}
              value={bodyField}
              onChange={(e) => setBodyField(e.target.value)}
            />
          </Box>

          <Box my={3}>
            <MainSolidButton
              disabled={loadingSend}
              loading={loadingSend}
              text="Enviar"
              icon={<SendRounded />}
              onClick={handleSend}
            />
          </Box>
        </Box>
      </DialogContent>
    </WideDialog>
  );
}
